import { ITextFieldManager } from "./ITextFieldManager";
import { IGGSTextFieldFactory } from "./IGGSTextFieldFactory";
import { ILanguageData } from "./ILanguageData";
import { GoodgameAdvancedTextFieldFactory } from "./GoodgameAdvancedTextFieldFactory";
import { AbstractTextContentVO, LocalizedNumberVO, LocalizedTextVO, TextVO, HTMLTextVO } from "@goodgamestudios/localization";
import { GGSCountryController } from "../../language/countryMapper/GGSCountryController";
import { InternalGGSTextFieldConfigVO } from "./vo/InternalGGSTextFieldConfigVO";
import { IBasicGGSTextField } from "../textfield/IBasicGGSTextField";
import { IInternalGGSTextField } from "../textfield/IInternalGGSTextField";
import { IGGSTextFieldBehaviour } from "../textfield/behaviour/IGGSTextFieldBehaviour";
import { DictionaryUtil } from "../../utils/DictionaryUtil";
import { TextFieldType, TextField, ColorTransform } from "@goodgamestudios/createjs-utilities";
import { getLogger } from "@goodgamestudios/logger";
import { TEXT_FIELDS_LOGGER, GGSTextDecoration } from "../../../../index";

import Event = createjs.Event;
const logger = getLogger(TEXT_FIELDS_LOGGER + ".GoodgameTextFieldManager");

/**
 * GoodgameTextFieldManager
 *
 * This class collects and manages all GoodgameTextFields application wide.
 */
export class GoodgameTextFieldManager implements ITextFieldManager {
    public static NAME: string = "GoodgameTextFieldManager";

    private static textFieldManager: ITextFieldManager;
    private _textFieldFactory: IGGSTextFieldFactory;
    private _languageData: ILanguageData;
    private _textFieldList: Map<TextField, IInternalGGSTextField>;

    // Behaviours
    private _mouseOutBehaviour: IGGSTextFieldBehaviour;
    private _mouseOverBehaviour: IGGSTextFieldBehaviour;

    private _maxRegisteredTextFields: number;

    // boundary font sizes for automatic resizing
    private _minimumAutoFitFontSize: number = 8;
    private _maximumAutoFitFontSize: number = 20;

    constructor() {
        this.resetTextFieldList();

        if (GoodgameTextFieldManager.textFieldManager) {
            throw new Error(GoodgameTextFieldManager.NAME + " Calling constructor not allowed! Use getInstance instead.");
        }
    }

    public static getInstance(): ITextFieldManager {
        if (!GoodgameTextFieldManager.textFieldManager) {
            GoodgameTextFieldManager.textFieldManager = new GoodgameTextFieldManager();
        }

        return (<ITextFieldManager>GoodgameTextFieldManager.textFieldManager);
    }

    private resetTextFieldList(): void {
        this._textFieldList = new Map<any, any>();
        this._maxRegisteredTextFields = 0;
    }

    public get countryController(): GGSCountryController {
        return GGSCountryController.instance;
    }

    public get textFieldFactory(): IGGSTextFieldFactory {
        if (!this._textFieldFactory) {
            this._textFieldFactory = new GoodgameAdvancedTextFieldFactory();
        }

        return this._textFieldFactory;
    }

    public set textFieldFactory(value: IGGSTextFieldFactory) {
        this._textFieldFactory = value;
    }

    public isTextFieldRegistered(textField: TextField): boolean {
        return DictionaryUtil.containsKey(this._textFieldList, textField);
    }

    /**
     * Returns a IBasicGGSTextField reference for the corresponding TextField.
     *
     * @return reference to textfield interface
     */
    public getTextField(textField: TextField): IBasicGGSTextField {
        return (<IBasicGGSTextField>this.getInternalTextField(textField));
    }

    private getInternalTextField(textField: TextField): IInternalGGSTextField {
        // Sanity check
        if (!textField) {
            logger.info("textField is null");
            return null;
        }

        let advancedTextField: IInternalGGSTextField = this._textFieldList.get(textField) || this.referenceTextField(textField);
        return advancedTextField;
    }

    private referenceTextField(textField: TextField): IInternalGGSTextField {
        let advancedTextField: IInternalGGSTextField = this.textFieldFactory.createAdvancedTextField(textField);
        this._textFieldList.set(textField, advancedTextField);
        return advancedTextField;
    }

    private onMouseOut(textField: IBasicGGSTextField): void {
        this._mouseOutBehaviour.execute();
    }

    private onMouseOver(textField: IBasicGGSTextField): void {
        this._mouseOverBehaviour.execute();
    }

    /**
     * unregisters a textfield
     * @param textField
     * @return true, if textfield was unregistered, false, if no textfield could be unregistered
     */
    public unregisterTextField(textField: TextField): boolean {
        let advancedTextField: IInternalGGSTextField;

        if (DictionaryUtil.containsKey(this._textFieldList, textField)) {
            advancedTextField = (<IInternalGGSTextField>this._textFieldList.get(textField));
            this._textFieldList.delete(textField);

            advancedTextField.unregister();
            return true;
        } else {
            return false;
        }
    }

    public unregisterTextFieldByReference(reference: IBasicGGSTextField): boolean {
        let advancedTextField: IInternalGGSTextField;
        if (DictionaryUtil.containsValue(this._textFieldList, reference)) {

            let key: any = DictionaryUtil.getKeyForValue(this._textFieldList, reference);

            advancedTextField = (<IInternalGGSTextField>this._textFieldList.get(key));
            this._textFieldList.delete(key);
            advancedTextField.unregister();

            return true;
        } else {
            return false;
        }
    }

    public get registeredTextFieldAmount(): number {
        return this._textFieldList.size;
    }

    public unregisterAllTextFields(): void {
        for (let advancedTextField of Array.from(this._textFieldList.values())) {
            this._textFieldList.delete(advancedTextField.originalTextField);
            advancedTextField.unregister();
            advancedTextField = null;
        }

        this.resetTextFieldList();
    }

    /**
     * set Textfield Values
     *
     * @param textField
     * @param id:String, the xml id
     * @param params
     * @param groupNumber: if the params should be converted to numbers with dots
     */
    public saveAndSetText(textField: TextField, id: string, params: any = null, groupParams: boolean = true): void {
        if (!textField) {
            logger.warn("textfield for id: " + id + " is missing");
            return;
        }

        if (id == null) {
            logger.warn("id for textField: " + textField.name + " is null");
            return;
        }

        let tf: IInternalGGSTextField = this.getInternalTextField(textField);
        //			tf.textContentVO = new LocalizedTextVO(id,params,groupParams);

        //			trace(NAME + ", saveAndSetText()");

        // TODO: Infeffizient das jedesmal wenn ein Textfeld mit saveAndSetText() retrieved wird aufzurufen?!
        // Nur aufrufen wenn noch kein text drin steht?
        this.updateText(tf, new LocalizedTextVO(id, params, groupParams));
    }

    /**
     * [Deprecated]
     * Displays static text (or already composed text content).
     *
     * Beware if want to display numbers:
     * <code>
     * // Localized number
     * GoodgameTextFieldManager.getInstance().setText(txtField,"8500",true);
     * // displays: 8.500
     *
     * // Already localized Number String
     * let localizedNumberStr:String = NumberStringHelper.groupString(15000.99,BasicModel.languageData.getTextById);
     * GoodgameTextFieldManager.getInstance().setText(txtField,localizedNumberStr,false);
     * // displays: 15.000,99
     *
     * // Number
     * GoodgameTextFieldManager.getInstance().setText(txtField,"1000",false);
     * // displays: 1000
     * </code>
     *
     * @param textField
     * @param text
     * @param groupNumber If true the "text" parameter will be interpreted as Number and will be localized. Set groupNumber to false if you want to display an already localized Number String.
     */
    public setText(textField: TextField, text: string, groupNumber: boolean = true): void {
        if (!textField) {
            logger.warn("textfield for id: " + text + " is missing");
            return;
        }

        if (text == null) {
            logger.warn("text for textField: " + textField.name + " is null");
            text = "";
        }

        let tf: IInternalGGSTextField = this.getInternalTextField(textField);

        const num = Number(text); //twice as fast as  cast to number that is allowed in Typescript

        // If groupNumber is false just display the text
        if (text == "" || !groupNumber || Number.isNaN(num)) {
            tf.textContentVO = new TextVO(text);
            return;
        }
        tf.textContentVO = new LocalizedNumberVO(num);

    }

    private updateText(tf: IInternalGGSTextField, textContentVO: AbstractTextContentVO): void {
        logger.debug("updateText ", tf, textContentVO);
        if (!this._languageData || !this._languageData.languageXMLLoaded) {
            logger.debug("Language XML not loaded yet, cannot update textfields." + this._languageData, this._languageData.languageXMLLoaded, tf.textContentVO);

            // Store VO but don't trigger localization due to language model is not ready yet
            textContentVO.triggerUpdate = false;
            tf.textContentVO = textContentVO;

            // Text cannot be composed yet, so hide them rather than showing the raw textIds
            tf.clearText();
            return;
        }
        else {
            textContentVO.triggerUpdate = true;
        }

        // Update behaviour listeners to e.g. change the cursor skin (only for input fields)
        if (tf.type == TextFieldType.INPUT) {
            tf.mouseOver.add(this.bindFunction(this.onMouseOver));
            tf.mouseOut.add(this.bindFunction(this.onMouseOut));
            // TODO: @HTML5 - these listeners are never removed!?
        }
        // Update text
        tf.displayLocalizedText(GGSCountryController.instance.currentCountry.ggsLanguageCode, textContentVO);
    }

    public registerInputTextField(textField: TextField, id: string, params: any = null, groupParams: boolean = true): void {
        return this.saveAndSetText(textField, id, params, groupParams);
    }

    private validateColor (color: string | number) {
        if (typeof color === "string") {
            return color;
        } else {
            return ColorTransform.colorNumToString(color);
        }
    }
    public registerTextField(textField: TextField, textContentVO: AbstractTextContentVO, config: InternalGGSTextFieldConfigVO = null): IBasicGGSTextField {
        if (!textField) {
            let debugTextContentVO: string = (textContentVO) ? " textContentVO: " + textContentVO.toString() : "";
            logger.error("TextField is null." + debugTextContentVO);
            return null;
        }

        if (textContentVO === null) {
            logger.error("textObject is null");
            return null;
        }

        if (config) {
            this.applyInternalGGSTextFieldConfiguration(config, textField);
        }
        if (textContentVO instanceof HTMLTextVO && textContentVO.linkFormats) {
            let formats = textContentVO.linkFormats;
            textField.htmlLinkFormats = {
                hover: { color: this.validateColor(formats.linkHoverFormat.color), underline: formats.linkHoverFormat.textDecoration === GGSTextDecoration.UNDERLINE},
                normal: { color: this.validateColor(formats.linkNormalFormat.color), underline: formats.linkNormalFormat.textDecoration === GGSTextDecoration.UNDERLINE},
                active: { color: this.validateColor(formats.linkActiveFormat.color), underline: formats.linkActiveFormat.textDecoration === GGSTextDecoration.UNDERLINE},
            };
        }

        // Retrieve TextField
        const tf: IInternalGGSTextField = this.getInternalTextField(textField);
        if (tf.model.type === "input") {
            // set `textContentVO` on the text field, otherwise it would just be passed around as parameters
            tf.textContentVO = textContentVO;
        }
        this.updateText(tf, textContentVO);
        return tf;
    }

    private applyInternalGGSTextFieldConfiguration(config: InternalGGSTextFieldConfigVO, textField: TextField): void {
        logger.debug("applyInternalGGSTextFieldConfiguration", config, textField);
        if (config.unRegisterAutomaticallyOnRemovedFromStage) {
            textField.addEventListener(Event.REMOVED_FROM_STAGE, this.bindFunction(this.onOriginalTextFieldRemoved));
        }
    }

    private onOriginalTextFieldRemoved(event: Event): void {
        //			trace(NAME + " onOriginalTextFieldRemoved() -> " + event.target.name);

        (<TextField>event.target).removeEventListener(Event.REMOVED_FROM_STAGE, this.bindFunction(this.onOriginalTextFieldRemoved));

        this.unregisterTextField((<TextField>event.target));
    }

    /**
     * rewrite text into textfield
     *
     * Update text composition
     */
    public updateTextInAllTextFields(): void {

        const txts = Array.from(this._textFieldList.values());
      //  txts.forEach( this.updateText)

        for (let tf of txts) {
            // // Trigger localization update    //      // this is being done again inside updateTExt!
            // if (tf.textContentVO) {
            //     tf.textContentVO.triggerUpdate = true;
            // }
            // else {
            //     logger.warn("textContentVO is null. TextField:" + tf.originalTextField.name);
            // }
            // //				trace("updateTextInAllTextFields() -> update: " + tf.originalTextField.name, tf.textContentVO.triggerUpdate);
            if (tf.textContentVO) {
                (tf as IInternalGGSTextField).textStrategy.activate();
                this.updateText(tf, tf.textContentVO);
            }
        }
    }

    public dispose(): void {
        // TODO: implement
    }

    public set mouseOutBehaviour(value: IGGSTextFieldBehaviour) {
        this._mouseOutBehaviour = value;
    }

    public set mouseOverBehaviour(value: IGGSTextFieldBehaviour) {
        this._mouseOverBehaviour = value;
    }

    public get languageData(): ILanguageData {
        return this._languageData;
    }

    public set languageData(model: ILanguageData) {
        this._languageData = model;
    }

    public get minimumAutoFitFontSize(): number {
        return this._minimumAutoFitFontSize;
    }

    public set minimumAutoFitFontSize(value: number) {
        this._minimumAutoFitFontSize = value;
    }

    public get maximumAutoFitFontSize(): number {
        return this._maximumAutoFitFontSize;
    }

    public set maximumAutoFitFontSize(value: number) {
        this._maximumAutoFitFontSize = value;
    }

    public get textFieldList(): Map<TextField, IInternalGGSTextField> {
        return this._textFieldList;
    }
}
